import {
  MANUAL_TASK_TYPES,
  TASK_TYPE_LABELS,
  type ManualTaskType,
  type NoteSource,
} from '@crm/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarClock, Check, ExternalLink, ListTodo, NotebookPen, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { describeError } from '../../lib/api.ts';
import { api, type MemberView } from '../../lib/client.ts';
import { useMembers } from '../../lib/record-data.ts';
import { cn } from '../../lib/utils.ts';
import { NoteTargetTree, noteTargetLabel } from '../notes/note-target-tree.tsx';
import { Button } from '../ui/button.tsx';
import { Input, Textarea } from '../ui/input.tsx';
import { RAIL } from '../ui/urgency-rail.ts';
import {
  blankTask,
  readDraft,
  writeDraft,
  type MeetingDraft,
  type Target,
  type TaskDraft,
} from './drafts.ts';
import { InlinePicker, PickerOptions } from './inline-picker.tsx';
import { isInternal, type Meeting } from './meeting.ts';
import { dayOf, noteBody, timeOf } from './note-body.ts';

/** As the Home cards' titles. */
const label = 'flex items-center gap-1.5 text-sm font-semibold';
/** Sections are divided by a rule, not boxed: in a 440px window two borders and paddings cost width. */
const divided = 'border-border flex flex-col gap-2 border-t pt-4';
const fieldLabel = 'text-muted-foreground flex min-w-0 flex-col gap-0.5 text-xs';

type Clock = { state: 'upcoming' | 'live' | 'ended'; text: string };

/** "Starts in 12 min", "Live · 23 min", "Ended 5 min ago": refreshed every 30 s. */
function useClock(meeting: Meeting): Clock {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(t);
  }, []);
  const start = Date.parse(meeting.start);
  const end = Date.parse(meeting.end);
  const min = (ms: number) => Math.max(0, Math.round(ms / 60_000));
  if (now < start) return { state: 'upcoming', text: `Starts in ${min(start - now)} min` };
  if (now <= end) return { state: 'live', text: `Live · ${min(now - start)} min` };
  return { state: 'ended', text: `Ended ${min(now - end)} min ago` };
}

const keyOf = (t: Target) => `${t.entityType}:${t.entityId}`;

/** Why a save failed; it stays until the next edit or save. */
function SaveError({ error }: { error: string | null }) {
  if (!error) return null;
  return (
    <p className="text-danger text-sm" role="alert">
      {error}
    </p>
  );
}

const TOAST_MS = 4000;

/**
 * What was just saved, over the bottom of the companion so nothing moves: one at a time (a new
 * save replaces it), gone after a few seconds.
 */
function useSavedToast() {
  const [toast, setToast] = useState<{ id: number; text: string } | null>(null);
  useEffect(() => {
    if (!toast) return;
    const t = window.setTimeout(() => setToast(null), TOAST_MS);
    return () => window.clearTimeout(t);
  }, [toast]);
  const show = (text: string) => setToast((prev) => ({ id: (prev?.id ?? 0) + 1, text }));
  // The live region stays mounted, so screen readers announce each new text put in it.
  const node = (
    <div role="status" className="pointer-events-none absolute inset-x-4 bottom-4 z-30">
      {toast && (
        <div
          key={toast.id}
          className="bg-foreground text-background flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm shadow-lg"
          data-testid="mc-toast"
        >
          <Check className="size-4 shrink-0" aria-hidden />
          <span className="min-w-0 truncate">{toast.text}</span>
        </div>
      )}
    </div>
  );
  return { show, node };
}

/** A task's assignee, due date and type, each labelled. */
function TaskFields({
  value,
  members,
  onChange,
}: {
  value: TaskDraft;
  members: MemberView[];
  onChange: (patch: Partial<TaskDraft>) => void;
}) {
  const assignees = [
    { value: null, label: 'Me' },
    ...members.map((m) => ({ value: m.id, label: m.name || m.email })),
  ];
  const types = MANUAL_TASK_TYPES.map((type) => ({ value: type, label: TASK_TYPE_LABELS[type] }));
  return (
    <div className="grid grid-cols-3 gap-1.5">
      <div className={fieldLabel}>
        Assignee
        <InlinePicker
          name="Assignee"
          searchLabel="Search people"
          display={assignees.find((a) => a.value === value.assigneeId)?.label ?? 'Me'}
          testId="mc-assignee-options"
        >
          {(query, close) => (
            <PickerOptions
              label="Assignee"
              options={assignees}
              selected={value.assigneeId}
              query={query}
              onPick={(assigneeId) => {
                close();
                onChange({ assigneeId });
              }}
            />
          )}
        </InlinePicker>
      </div>
      <label className={fieldLabel}>
        Due date
        <Input
          type="date"
          aria-label="Due date"
          value={value.due ?? ''}
          onChange={(e) => onChange({ due: e.target.value || null })}
        />
      </label>
      <div className={fieldLabel}>
        Type
        <InlinePicker
          name="Type"
          searchLabel="Search types"
          display={TASK_TYPE_LABELS[value.type]}
          align="end"
          testId="mc-type-options"
        >
          {(query, close) => (
            <PickerOptions
              label="Type"
              options={types}
              selected={value.type}
              query={query}
              onPick={(type: ManualTaskType) => {
                close();
                onChange({ type });
              }}
            />
          )}
        </InlinePicker>
      </div>
    </div>
  );
}

/**
 * The client's project in an `InlinePicker`: the attendees' projects until 3 letters are typed,
 * then the projects whose name matches (`GET /projects?q=`).
 */
function ProjectPicker({
  display,
  selected,
  suggestions,
  onPick,
}: {
  display: string;
  selected: string | null;
  suggestions: Array<{ id: string; name: string }>;
  onPick: (id: string) => void;
}) {
  return (
    <InlinePicker
      name="Client's project"
      searchLabel="Search projects"
      display={display}
      testId="mc-project-options"
    >
      {(query, close) => (
        <ProjectOptions
          query={query}
          selected={selected}
          suggestions={suggestions}
          onPick={(id) => {
            close();
            onPick(id);
          }}
        />
      )}
    </InlinePicker>
  );
}

function ProjectOptions({
  query,
  selected,
  suggestions,
  onPick,
}: {
  query: string;
  selected: string | null;
  suggestions: Array<{ id: string; name: string }>;
  onPick: (id: string) => void;
}) {
  const found = useQuery({
    queryKey: ['meeting-companion', 'projects', query],
    enabled: !!query,
    queryFn: () => api.projects.list({ q: query, limit: 20 }),
  });
  const hint = (text: string) => (
    <p className="text-muted-foreground px-2 py-1.5 text-sm">{text}</p>
  );
  if (!query)
    return (
      <>
        {suggestions.length > 0 && (
          <PickerOptions
            label="The attendees' projects"
            options={suggestions.map((p) => ({ value: p.id, label: p.name }))}
            selected={selected}
            query=""
            onPick={onPick}
          />
        )}
        {hint('Type 3 letters to search every project.')}
      </>
    );
  if (found.isPending) return hint('Searching…');
  if (found.isError) return hint(describeError(found.error));
  return (
    <PickerOptions
      label="Projects"
      options={found.data.items.map((p) => ({ value: p.id, label: p.name }))}
      selected={selected}
      query=""
      onPick={onPick}
    />
  );
}

/** "Save note to": the project page's Add note tree (`NoteTargetTree`) in an `InlinePicker`. */
function NoteTargetPicker({
  sources,
  value,
  onChange,
}: {
  sources: NoteSource[];
  value: NoteSource | undefined;
  onChange: (t: Target) => void;
}) {
  return (
    <InlinePicker
      name="Save note to"
      searchLabel="Search records"
      display={value ? noteTargetLabel(value) : 'Choose a record'}
      testId="mc-note-target-tree"
    >
      {(query, close) => (
        <NoteTargetTree
          sources={sources}
          selected={value}
          query={query}
          label="Save note to"
          onPick={(src) => {
            close();
            onChange({ entityType: src.entityType, entityId: src.entityId });
          }}
        />
      )}
    </InlinePicker>
  );
}

/**
 * The Meeting Companion (TS-1963): what an account manager keeps open beside a client
 * call, in its own floating window (`meeting-companion-host.tsx`).
 *
 *  - The meeting and how long it has run, with Join.
 *  - The client's project, found from the attendees' emails (`POST /projects:lookup`) or picked
 *    by hand.
 *  - NOTE, saved by its own "Save note" to the record picked right under it: the project or any
 *    record under it (its company, contacts, deals, jobs, candidates, as the project page's "Add
 *    note" tree).
 *  - TASKS, a separate section: each saved by its own "Save task", straight away, on the PROJECT
 *    (the record page that shows tasks; contacts, deals and jobs do not), with an assignee, a due
 *    date and a type. Each save is confirmed by one short toast (`useSavedToast`).
 *
 * What is typed and not saved is a draft in this browser (`drafts.ts`). Plain elements only, no
 * Radix menus or dialogs: in a Picture-in-Picture window those portal to the main tab's body
 * (see `external-window-portal.tsx`).
 */
export function MeetingCompanion({ meeting, onClose }: { meeting: Meeting; onClose: () => void }) {
  const qc = useQueryClient();
  const clock = useClock(meeting);
  const { members } = useMembers();
  const [draft, setDraft] = useState<MeetingDraft>(() => readDraft(meeting.id));
  const [noteError, setNoteError] = useState<string | null>(null);
  const [taskError, setTaskError] = useState<string | null>(null);
  const toast = useSavedToast();
  const firstWrite = useRef(true);
  const unwritten = useRef<MeetingDraft | null>(null);

  // The draft is written as it changes (debounced), so a closed window loses nothing.
  useEffect(() => {
    if (firstWrite.current) {
      firstWrite.current = false;
      return;
    }
    unwritten.current = draft;
    const t = window.setTimeout(() => {
      writeDraft(meeting.id, draft);
      unwritten.current = null;
    }, 400);
    return () => window.clearTimeout(t);
  }, [draft, meeting.id]);
  // A close, or a switch to another meeting, writes what the debounce had not yet.
  useEffect(
    () => () => {
      if (unwritten.current) writeDraft(meeting.id, unwritten.current);
      unwritten.current = null;
    },
    [meeting.id],
  );
  const edit = (patch: Partial<MeetingDraft>) => setDraft((d) => ({ ...d, ...patch }));
  const editTask = (patch: Partial<TaskDraft>) => {
    setTaskError(null);
    setDraft((d) => ({ ...d, task: { ...d.task, ...patch } }));
  };

  // The client's project: each external attendee's email through the project lookup.
  const emails = useMemo(
    () =>
      [...new Set(meeting.attendees.map((a) => a.email.toLowerCase()))].filter(
        (e) => !isInternal(e),
      ),
    [meeting.attendees],
  );
  const lookup = useQuery({
    queryKey: ['meeting-companion', 'lookup', emails],
    enabled: emails.length > 0 && !meeting.projectId,
    queryFn: async () => {
      const found = new Map<string, { id: string; name: string }>();
      for (const email of emails)
        for (const item of (await api.projects.lookup(email)).items)
          found.set(item.project.id, { id: item.project.id, name: item.project.name });
      return [...found.values()];
    },
  });
  const matches = lookup.data ?? [];
  const projectId =
    draft.projectId ?? meeting.projectId ?? (matches.length === 1 ? matches[0]!.id : null);
  const project = useQuery({
    queryKey: ['project', projectId],
    enabled: !!projectId,
    queryFn: () => api.projects.get(projectId!),
  });
  const pickProject = (id: string) => edit({ projectId: id, noteTarget: null });

  // Where the note can go: the project and everything under it. Tasks go on the project only.
  const sources = useQuery({
    queryKey: ['notes', 'sources', 'projects', projectId],
    enabled: !!projectId,
    queryFn: () => api.notes.sources('projects', projectId!),
  });
  const writable = (sources.data ?? []).filter((s) => s.canWrite);
  const projectSource = writable.find((s) => s.entityType === 'projects');
  const noteTo =
    writable.find((s) => draft.noteTarget && keyOf(s) === keyOf(draft.noteTarget)) ?? projectSource;
  const active = members.filter((m) => m.status === 'active');
  const refresh = (keys: string[][]) =>
    Promise.all(keys.map((queryKey) => qc.invalidateQueries({ queryKey })));

  const saveNote = useMutation({
    mutationFn: ({ to, notes }: { to: NonNullable<typeof noteTo>; notes: string }) =>
      api.notes.create({
        entityType: to.entityType,
        entityId: to.entityId,
        body: noteBody(meeting, { ...draft, notes }),
        pinned: false,
      }),
    onSuccess: async (_created, { to, notes }) => {
      setNoteError(null);
      toast.show(`Note saved to ${to.label}.`);
      // Clears what was sent; anything typed while it was saving stays.
      setDraft((d) => ({
        ...d,
        notes: d.notes.startsWith(notes) ? d.notes.slice(notes.length).trimStart() : d.notes,
      }));
      await refresh([['notes'], ['activities']]);
    },
    onError: (err) => setNoteError(describeError(err)),
  });

  const saveTask = useMutation({
    mutationFn: (t: TaskDraft) =>
      api.tasks.create({
        entityType: 'projects',
        entityId: projectSource!.entityId,
        title: t.title.trim(),
        type: t.type,
        ...(t.assigneeId ? { assigneeId: t.assigneeId } : {}),
        // The end of the assignee's working day on the chosen date.
        ...(t.due ? { dueAt: new Date(`${t.due}T17:00:00`).toISOString() } : {}),
      }),
    onSuccess: async (_created, t) => {
      setTaskError(null);
      toast.show(`Task saved: ${t.title.trim()}`);
      // A fresh task, keeping the assignee: several tasks for one person are common in a call.
      // Not if it was changed while saving: that is the next task already.
      setDraft((d) =>
        d.task.title === t.title ? { ...d, task: { ...blankTask(), assigneeId: t.assigneeId } } : d,
      );
      await refresh([['tasks'], ['activities']]);
    },
    onError: (err) => setTaskError(describeError(err)),
  });

  return (
    <div className="relative flex h-full flex-col" data-testid="meeting-companion">
      {/* The meeting. */}
      {/* The Calendar's brand rail for the next meeting: the one you clicked keeps its mark here. */}
      <header
        className={cn(
          'border-border flex flex-col gap-2 border-b px-4 py-3',
          clock.state !== 'ended' && RAIL.brand,
        )}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="truncate font-semibold" title={meeting.title}>
              {meeting.title}
            </p>
            <p className="text-muted-foreground mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
              <span className="flex items-center gap-1.5">
                <CalendarClock className="size-3.5" aria-hidden />
                {dayOf(meeting.start)}, {timeOf(meeting.start)}–{timeOf(meeting.end)}
              </span>
              <span
                className={cn(
                  'inline-flex items-center gap-1 rounded-full px-2 leading-5 font-medium',
                  clock.state === 'live'
                    ? 'bg-brand-soft text-brand'
                    : clock.state === 'upcoming'
                      ? 'bg-muted text-foreground'
                      : 'bg-muted text-muted-foreground',
                )}
                data-testid="mc-clock"
                data-state={clock.state}
              >
                {clock.state === 'live' && (
                  <span className="bg-brand size-1.5 rounded-full" aria-hidden />
                )}
                {clock.text}
              </span>
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            <Button size="sm" variant="ghost" aria-label="Close the companion" onClick={onClose}>
              <X />
            </Button>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          {meeting.joinUrl && (
            <Button size="sm" asChild>
              <a href={meeting.joinUrl} target="_blank" rel="noreferrer">
                <ExternalLink /> Join
              </a>
            </Button>
          )}
          {meeting.attendees.map((a) => (
            <span
              key={a.email}
              className={cn(
                'rounded-full border px-2 py-0.5 text-xs',
                isInternal(a.email) ? 'text-muted-foreground border-border' : 'border-brand/40',
              )}
              title={a.email}
            >
              {a.name ?? a.email}
            </span>
          ))}
        </div>
      </header>

      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-4 py-3">
        {/* The client: the project the note and tasks belong under. */}
        <section className="flex flex-col gap-2" aria-label="Client">
          <h3 className={label}>Client</h3>
          {lookup.isLoading && <p className="text-muted-foreground text-sm">Finding the client…</p>}
          {!lookup.isLoading && !projectId && (
            <p className="text-muted-foreground text-xs">
              {matches.length > 1
                ? 'The attendees are on several projects: which one is this meeting about?'
                : 'No project has these attendees. Search for the client:'}
            </p>
          )}
          {!lookup.isLoading && (
            <div className="flex items-center gap-1">
              <ProjectPicker
                display={
                  project.data?.name ??
                  // A saved draft can name a project since deleted or out of reach.
                  (!projectId
                    ? 'Choose a project'
                    : project.isError
                      ? 'Project unavailable: choose another'
                      : 'Loading…')
                }
                selected={projectId}
                suggestions={matches}
                onPick={pickProject}
              />
              {project.data && (
                <a
                  // Absolute: inside a Picture-in-Picture window a relative link resolves
                  // against its blank document, not the app.
                  href={new URL(`/projects/${project.data.id}`, window.location.origin).href}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={`Open ${project.data.name}`}
                  title="Open the project"
                  className="text-muted-foreground hover:text-foreground shrink-0 rounded-md p-1.5"
                >
                  <ExternalLink className="size-4" aria-hidden />
                </a>
              )}
            </div>
          )}
        </section>

        {/* The note: where it goes, and its own save, right under it. */}
        <section className={cn(divided, 'min-h-48 flex-auto shrink-0')} aria-label="Note">
          <h3 className={label}>
            <NotebookPen className="text-muted-foreground size-4" aria-hidden /> Note
          </h3>
          <Textarea
            aria-label="Meeting notes"
            placeholder="What was said, decided, asked… Markdown: **bold**, lists, links"
            className="min-h-36 flex-1 resize-none"
            value={draft.notes}
            onChange={(e) => {
              setNoteError(null);
              edit({ notes: e.target.value });
            }}
          />
          {writable.length > 0 && (
            <div className="flex items-center gap-2">
              <div className="flex min-w-0 flex-1 items-center gap-2 text-sm">
                <span className="text-muted-foreground shrink-0" aria-hidden>
                  Save note to
                </span>
                <NoteTargetPicker
                  sources={writable}
                  value={noteTo}
                  onChange={(t) => {
                    edit({ noteTarget: t });
                  }}
                />
              </div>
              <Button
                className="shrink-0"
                onClick={() => saveNote.mutate({ to: noteTo!, notes: draft.notes })}
                disabled={!noteTo || !draft.notes.trim() || saveNote.isPending}
                loading={saveNote.isPending}
              >
                Save note
              </Button>
            </div>
          )}
          <SaveError error={noteError} />
        </section>

        {/* Tasks: their own section, each saved by its own button, on the project. */}
        <section className={cn(divided, 'shrink-0')} aria-label="Tasks">
          <h3 className={label}>
            <ListTodo className="text-muted-foreground size-4" aria-hidden /> Tasks
            {projectSource && (
              <span className="text-muted-foreground text-xs font-normal">
                Saved on the client&apos;s project
              </span>
            )}
          </h3>
          <form
            className="flex flex-col gap-1.5"
            aria-label="New task"
            onSubmit={(e) => {
              e.preventDefault();
              if (draft.task.title.trim() && projectSource) saveTask.mutate(draft.task);
            }}
          >
            <Input
              aria-label="Task"
              placeholder="Send the shortlist by Friday…"
              value={draft.task.title}
              onChange={(e) => editTask({ title: e.target.value })}
            />
            <TaskFields value={draft.task} members={active} onChange={editTask} />
            <Button
              type="submit"
              className="self-end"
              disabled={!projectSource || !draft.task.title.trim() || saveTask.isPending}
              loading={saveTask.isPending}
            >
              Save task
            </Button>
          </form>
          <SaveError error={taskError} />
        </section>
      </div>
      {toast.node}
    </div>
  );
}
